/**
 * Turns the "UMLC search" text on the Workflow Executions page into MoMaS ListWorkflowExecutions query params.
 *
 * Supported input, comma separated:
 *   shiqiang                         -> same as requester:shiqiang
 *   requester:shiqiang               -> only executions started by shiqiang
 *   status:failed                    -> status filter (case-insensitive)
 *   workflow_template_name:MyModel
 *   workflow_template_version:1.0.0
 *   started_date_after:2026-09-01    -> any date the browser can parse, sent as ISO 8601
 *   started_date_before:09/30/2026
 * Empty input returns the default "My Team" view.
 */

export const SUPPORTED_KEYS = [
    "requester",
    "status",
    "workflow_template_name",
    "workflow_template_version",
    "started_date_after",
    "started_date_before",
];

// Must match WorkflowExecutionStatus in MODSModelWorkflowManagementServiceModel.
export const SUPPORTED_STATUSES = [
    "VALIDATING",
    "VALIDATED",
    "PROVISIONING",
    "PROVISIONED",
    "STARTING",
    "EXECUTING",
    "STOPPING",
    "COMPLETE",
    "BLOCKED",
    "FAILED",
    "STOPPED",
    "SUCCEEDED",
];

const DATE_KEYS = ["started_date_after", "started_date_before"];

/**
 * @param {string} searchText What the user typed.
 * @param {string} currentUser Alias of the signed-in user, used for the default team view.
 * @param {number} pageSize Current table page size.
 * @returns {{query: URLSearchParams} | {error: string}}
 */
export function buildExecutionSearchQuery(searchText, currentUser, pageSize) {
    const filters = {};
    const tokens = (searchText || "")
        .split(",")
        .map((token) => token.trim())
        .filter((token) => token.length > 0);

    for (const token of tokens) {
        const separator = token.indexOf(":");
        // A bare word is the most common search: an alias.
        const key = separator === -1 ? "requester" : token.slice(0, separator).trim().toLowerCase();
        let value = separator === -1 ? token : token.slice(separator + 1).trim();

        if (!SUPPORTED_KEYS.includes(key)) {
            return { error: `Unknown search key "${key}". Supported keys: ${SUPPORTED_KEYS.join(", ")}.` };
        }
        if (value.length === 0) {
            return { error: `Missing a value for "${key}".` };
        }
        if (key in filters) {
            return { error: `"${key}" can only be used once per search.` };
        }

        if (key === "requester") {
            value = value.replace(/@$/, "");
        } else if (key === "status") {
            value = value.toUpperCase();
            if (!SUPPORTED_STATUSES.includes(value)) {
                return { error: `Unknown status "${value}". Supported statuses: ${SUPPORTED_STATUSES.join(", ")}.` };
            }
        } else if (DATE_KEYS.includes(key)) {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return { error: `"${value}" is not a valid date for "${key}". Example: ${key}:2026-09-01.` };
            }
            value = date.toISOString();
        }
        filters[key] = value;
    }

    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        query.append("filter_type", key.toUpperCase());
        query.append(key, value);
    }
    if ("requester" in filters) {
        // Without requester_only, MoMaS returns the requester's whole team.
        query.append("requester_only", "true");
    } else {
        // MoMaS always needs a requester to resolve the team; default to the signed-in user's team.
        query.append("filter_type", "REQUESTER");
        query.append("requester", currentUser);
    }
    query.append("page_size", String(pageSize));
    query.append("page_index", "0");
    return { query };
}
